<template>
  <v-dialog
    v-if="dialog"
    :key="reportId"
    v-model="dialog"
    :max-width="modalLayout.LG"
    class="dialog-content"
    v-bind="modalP2FormViewport"
    :data-testid="getTestId('report-dialog')"
    @click:outside="handleClose"
    @close="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="report-form-dialog-card">
      <v-form ref="form" v-model="valid" @submit.prevent="submit">
        <CrudFormDialogHeader
          :kicker="$t('common.reports')"
          :title="formTitle"
          show-close
          @close="handleClose"
        />
        <v-card-text class="report-form-dialog-body pt-5 text-body-2">
          <v-row>
            <v-col cols="6" class="pl-6">
              <!-- 사이트 선택 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('site.label') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-combobox
                    v-model="localEditedItem.site"
                    :items="formattedSites"
                    item-text="text"
                    item-value="value"
                    hide-details="auto"
                    dense
                    outlined
                    :placeholder="$t('site.search')"
                    append-icon=""
                    :rules="[requiredComboBoxRule]"
                    :data-testid="getTestId('report-site-select')"
                    return-object
                  />
                </v-col>
              </v-row>

              <!-- 정밀검사 명 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('inspection.name') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.name"
                    hide-details="auto"
                    dense
                    outlined
                    :rules="[requiredRule]"
                    required
                    :data-testid="getTestId('report-name-input')"
                  />
                </v-col>
              </v-row>

              <!-- 정밀검사 일자 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('inspection.date') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-menu
                    v-model="examineDatepicker"
                    :close-on-content-click="false"
                    offset-y
                    min-width="auto"
                    hide-details="auto"
                    dense
                  >
                    <template slot="activator" slot-scope="{ on, attrs }">
                      <v-text-field
                        v-model="localEditedItem.examineDate"
                        readonly
                        v-bind="attrs"
                        :rules="[requiredRule]"
                        :clearable="false"
                        hide-details="auto"
                        dense
                        outlined
                        append-icon="mdi-calendar"
                        :data-testid="getTestId('report-examine-date-input')"
                        v-on="on"
                        @click:append="examineDatepicker = !examineDatepicker"
                      />
                    </template>
                    <v-date-picker
                      v-model="localEditedItem.examineDate"
                      no-title
                      scrollable
                      color="primary"
                      :rules="[requiredRule]"
                      required
                      @input="examineDatepicker = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <!-- 정밀검사 시간 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('inspection.time') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-combobox
                    v-model="localEditedItem.examineHour"
                    :items="examineHours"
                    hide-details="auto"
                    dense
                    outlined
                    :placeholder="$t('inspection.time')"
                  />
                </v-col>
              </v-row>

              <!-- 수행 업체명 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('inspection.company') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectionCompanyName"
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 드론(UAV) -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('equipment.drone') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectionDrone"
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 카메라 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('equipment.camera') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectionCamera"
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 카메라 피치 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('equipment.cameraPitch') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model.number="localEditedItem.cameraPitch"
                    type="number"
                    hide-details="auto"
                    dense
                    outlined
                    suffix="°"
                    :rules="[cameraPitchRule]"
                  />
                </v-col>
              </v-row>

              <!-- 방사조도 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('equipment.solarRadiation') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.irradiance"
                    hide-details="auto"
                    dense
                    outlined
                    suffix="W/m2"
                    :rules="[numberRule]"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="6" class="pr-5">
              <!-- 담당자명 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('inspection.inspectorName') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectorName"
                    :data-testid="getTestId('report-inspector-name-input')"
                    :rules="[requiredRule]"
                    required
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 담당자 연락처 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('inspection.inspectorPhone') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectorPhoneNo"
                    :data-testid="getTestId('report-inspector-phone-input')"
                    :rules="[numberRule]"
                    required
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 담당자 이메일 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2 required">
                  <label>{{ $t('inspection.inspectorEmail') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-text-field
                    v-model="localEditedItem.inspectorEmailAddr"
                    :data-testid="getTestId('report-inspector-email-input')"
                    :rules="[requiredRule, emailRules]"
                    required
                    hide-details="auto"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>

              <!-- 설명 -->
              <v-row align="baseline">
                <v-col cols="3" class="nameField px-1 py-2">
                  <label>{{ $t('common.description') }}</label>
                </v-col>
                <v-col cols="8" class="valueField px-1 py-2">
                  <v-textarea
                    v-model="localEditedItem.reportDesc"
                    :data-testid="getTestId('report-description-input')"
                    outlined
                    rows="8"
                    hide-details="auto"
                    dense
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="report-form-dialog-actions px-4 pb-4 pt-0">
          <v-spacer />
          <v-btn
            outlined
            class="solvit-crud-outline-btn"
            :data-testid="getTestId('report-cancel-button')"
            @click="handleClose"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--primary ml-2"
            type="submit"
            :data-testid="getTestId('report-save-button')"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
  import Api from '@/apis/Api';
  import { getTestId } from '@/utils/testUtils';
  import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
  import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

  export default {
    name: 'UploadWrapper',
    components: {
      CrudFormDialogHeader
    },
    props: {
      dialog: {
        type: Boolean,
        required: true
      },
      editedItem: {
        type: Object,
        required: true
      },
      sites: {
        type: Array,
        required: true
      },
      examineHours: {
        type: Array,
        required: true
      },
      reportId: {
        type: Number,
        default: 0
      },
      mode: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        modalLayout: MODAL_MAX_WIDTH,
        modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
        valid: true,
        examineDatepicker: false,
        emailErrors: '',
        sortedSites: this.$props.sites.filter((a, b) => a.name.localeCompare(b.name)),
        localEditedItem: { ...this.$props.editedItem }
      };
    },
    watch: {
      sites() {
        // sites가 변경되면 사이트 매칭 다시 시도
        this.matchSiteFromFormattedSites();
      },
      sortedSites() {},
      editedItem: {
        handler(newVal) {
          this.localEditedItem = { ...newVal };
          // 사이트 정보가 있으면 formattedSites에서 동일한 객체를 찾아서 할당
          this.$nextTick(() => {
            this.matchSiteFromFormattedSites();
          });
        },
        deep: true,
        immediate: true
      },
      reportId: {
        handler(newVal) {
          if (newVal) {
            this.fetchReport(newVal);
          }
        }
      }
    },
    computed: {
      formTitle() {
        return this.$props.mode === 'edit' ? this.$t('common.edit') : this.$t('common.addNew');
      },
      emailRules() {
        return v => (!!v && /.+@.+\..+/.test(v)) || this.$t('validation.emailFormat');
      },
      requiredRule() {
        return v => !!v || this.$t('common.required');
      },
      requiredComboBoxRule() {
        return v => !!v?.value || this.$t('validation.selectSite');
      },
      numberRule() {
        return v => !isNaN(v) || this.$t('common.numericOnly');
      },
      cameraPitchRule() {
        return v => {
          const num = Number(v);
          if (v === '' || v === null || v === undefined) return true;
          if (!Number.isFinite(num)) return this.$t('common.numericOnly');
          if (num < -90 || num > 90) return this.$t('validation.cameraPitchRange');
          return true;
        };
      },
      formattedSites() {
        const sortedSites = [...this.$props.sites].sort((a, b) => a.name.localeCompare(b.name));

        const formattedSites = sortedSites.map(site => {
          return {
            text: site.name,
            value: site.id
          };
        });

        return formattedSites;
      }
    },
    methods: {
      getTestId(testId) {
        return getTestId(testId);
      },
      /**
       * localEditedItem.site를 formattedSites에서 매칭하여 동일한 객체 참조로 설정
       */
      matchSiteFromFormattedSites() {
        if (!this.localEditedItem || !this.localEditedItem.site) {
          return;
        }

        if (!this.formattedSites || this.formattedSites.length === 0) {
          return;
        }

        const currentSite = this.localEditedItem.site;
        const matchedSite = this.formattedSites.find(site => {
          // value로 비교 (숫자 또는 문자열)
          if (site.value === currentSite.value) {
            return true;
          }
          // id로 비교
          if (site.value === currentSite.id) {
            return true;
          }
          // text로 비교
          if (site.text === currentSite.text || site.text === currentSite.name) {
            return true;
          }
          return false;
        });

        if (matchedSite) {
          this.$set(this.localEditedItem, 'site', matchedSite);
        }
      },
      handleClose() {
        this.resetForm();
        this.$emit('close');
      },
      handleSave(reportId) {
        if (this.$props.mode === 'add') {
          this.$props.reportId = reportId;
        }
        this.$emit('saved', reportId);
      },
      onInitialize() {
        this.$emit('initialize');
      },
      validateForm() {
        this.$refs.form.validate();
        return this.valid;
      },
      submit() {
        if (!this.validateForm()) {
          return;
        }
        this.save();
      },
      save() {
        console.log('테스트 localEditedItem:', this.localEditedItem, this.reportId);

        const formJson = {};
        formJson.siteSeq = this.localEditedItem.site?.value ?? '';
        console.log(
          '테스트 siteSeq:',
          formJson.siteSeq,
          this.localEditedItem.site?.value,
          this.localEditedItem
        );
        formJson.reportName = this.localEditedItem.name;
        formJson.reportDesc = this.localEditedItem.reportDesc;
        formJson.examineDate = this.localEditedItem.examineDate;
        formJson.examineHour = this.localEditedItem.examineHour?.value ?? 0;
        formJson.inspectionCompanyName = this.localEditedItem.inspectionCompanyName;
        formJson.inspectionDrone = this.localEditedItem.inspectionDrone;
        formJson.inspectionCamera = this.localEditedItem.inspectionCamera;
        formJson.inspectorName = this.localEditedItem.inspectorName;
        formJson.inspectorPhoneNo = this.localEditedItem.inspectorPhoneNo;
        formJson.inspectorEmailAddr = this.localEditedItem.inspectorEmailAddr;
        formJson.irradiance = parseInt(this.localEditedItem.irradiance) ?? 0;
        formJson.cameraPitch = this.localEditedItem.cameraPitch ?? -90;

        //추가
        if (this.$props.mode == 'edit') {
          Api.update(`/examine-report/${this.$props.reportId}`, formJson, true)
            .then(response => {
              this.$emit('saved', this.reportId);
            })
            .catch(e => {
              this.$emit('error', this.$t('common.sendFailed'), e);
            });
          //수정
        } else if (this.$props.mode == 'add') {
          Api.create('/examine-report', formJson)
            .then(response => {
              const reportId = response.data.id || this.reportId;
              this.$emit('saved', this.reportId);
            })
            .catch(() => {
              console.log('전송 실패');
              this.$emit('error', this.$t('common.sendFailed'));
            });
        }
      },
      getInitialEditedItem() {
        return {
          site: null,
          name: '',
          examineDate: '',
          examineHour: '',
          inspectionCompanyName: '',
          inspectionDrone: '',
          inspectionCamera: '',
          irradiance: '',
          cameraPitch: -90,
          inspectorName: '',
          inspectorPhoneNo: '',
          inspectorEmailAddr: '',
          reportDesc: ''
        };
      },
      resetForm() {
        this.localEditedItem = this.getInitialEditedItem();
      }
    }
  };
</script>

<style scoped>
  .report-form-dialog-card {
    background-color: var(--v-surface-base) !important;
    border-color: var(--v-borderSubtle-base) !important;
  }

  .report-form-dialog-body .nameField {
    color: var(--v-secondary-base);
    font-weight: 500;
    font-size: 0.8125rem;
  }

  .report-form-dialog-body .nameField.required::after {
    content: '*';
    color: var(--v-error-base);
    margin-left: 4px;
  }

  .report-form-dialog-actions {
    border-top: 1px solid var(--v-borderSubtle-base);
  }
</style>

<style>
  /* UploadWrapper 레거시 전역 — 다이얼로그 높이만 유지 (필수 * 는 scoped로 이전) */
  .v-dialog__content {
    max-height: 100%;
  }
  .v-dialog:not(.v-dialog--fullscreen) {
    max-height: 100%;
  }
</style>
